import { WEATHER_TILE_PREFIX } from "@nova/constants"
import { config } from "@nova/configuration/node"
import { createSign } from "crypto"

const CF_COOKIE_EXPIRY_SECONDS = 60 * 60 // 1 hour

export type CloudfrontCookies = {
    "CloudFront-Key-Pair-Id": string
    "CloudFront-Policy": string
    "CloudFront-Signature": string
}

const base64UrlEncode = (value: string) =>
    value.replace(/\+/g, "-").replace(/=/g, "_").replace(/\//g, "~")

// Converts a data-bucket S3 key to its CloudFront URL.
// The /{regionPrefix}/data/ behavior strips the first two path segments before routing to S3.
export const buildCloudfrontDataUrl = (domain: string, regionPrefix: string, s3Path: string) =>
    `https://${domain}/${regionPrefix}/data/${s3Path}`

export const buildCloudfrontAgoraUrl = (domain: string, regionPrefix: string, s3Path: string) =>
    `https://${domain}/${regionPrefix}/agora/${s3Path}`

const signPolicy = (resourceUrl: string, epochTime: number, privateKey: string) => {
    const policy = JSON.stringify({
        Statement: [
            { Resource: resourceUrl, Condition: { DateLessThan: { "AWS:EpochTime": epochTime } } },
        ],
    })
    const sign = createSign("RSA-SHA1")
    sign.update(policy)
    return {
        "CloudFront-Policy": base64UrlEncode(Buffer.from(policy).toString("base64")),
        "CloudFront-Signature": base64UrlEncode(sign.sign(privateKey, "base64")),
    }
}

export type CloudfrontCookieEntry = { name: string; value: string; path: string }

export const signCloudfrontCookies = ({
    resourceUrls,
    keyPairId,
    privateKey,
    expiresAt = new Date(Date.now() + CF_COOKIE_EXPIRY_SECONDS * 1000),
}: {
    resourceUrls: { bucket: string; path: string }[]
    keyPairId: string
    privateKey: string
    expiresAt?: Date
}): CloudfrontCookieEntry[] => {
    const epochTime = Math.floor(expiresAt.getTime() / 1000)
    const domain = config.get("cloudfrontDomain")
    return [
        { name: "CloudFront-Key-Pair-Id", value: keyPairId, path: "/" },
        ...resourceUrls.flatMap(({ bucket, path }) => {
            const resourceUrl = `https://${domain}/${bucket}/${path}`
            const cookiePath = new URL(resourceUrl).pathname.replace(/\*$/, "")
            const { "CloudFront-Policy": policy, "CloudFront-Signature": signature } = signPolicy(
                resourceUrl,
                epochTime,
                privateKey
            )
            return [
                { name: "CloudFront-Policy", value: policy, path: cookiePath },
                { name: "CloudFront-Signature", value: signature, path: cookiePath },
            ]
        }),
    ]
}

export const orgPaths = (orgId: string, regionPrefix: string) => [
    { bucket: "ca/data", path: "public/user/*" },
    { bucket: "ca/weather", path: `${WEATHER_TILE_PREFIX}/*` },
    { bucket: `${regionPrefix}/data`, path: `public/org/${orgId}/assets/*` },
    { bucket: `${regionPrefix}/data`, path: `public/org/${orgId}/pilot-tracking/*` },
]

export const projectPaths = (orgId: string, projectId: string, regionPrefix: string) => [
    { bucket: "ca/data", path: "public/user/*" },
    { bucket: "ca/weather", path: `${WEATHER_TILE_PREFIX}/*` },
    { bucket: `${regionPrefix}/data`, path: `public/org/${orgId}/assets/*` },
    { bucket: `${regionPrefix}/data`, path: `public/org/${orgId}/pilot-tracking/*` },
    { bucket: `${regionPrefix}/data`, path: `public/org/${orgId}/fire/${projectId}/*` },
    { bucket: `${regionPrefix}/data`, path: `public/org/${orgId}/project/${projectId}/*` },
    { bucket: `${regionPrefix}/agora`, path: "*" },
]
